import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, DataTable, StatusPill, Section, Loading } from "../components/Common";

export default function StudentDetail() {
  const { id, school } = useParams();
  const [student, setStudent] = useState(null);
  const [report, setReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [attendance, setAttendance] = useState([]);

  useEffect(() => {
    api.student(id).then(({ data }) => setStudent(data));
    api.reportCard(id).then(({ data }) => setReport(data)).catch(() => {});
    api.invoices({ student_id: id }).then(({ data }) => setInvoices(data));
    api.attendance({ student_id: id }).then(({ data }) => setAttendance(data.slice(0, 30)));
  }, [id]);

  if (!student) return <Loading />;

  return (
    <div data-testid="student-detail">
      <div className="mb-4"><Link to={`/education360/${school}/students`} className="text-xs text-slate-500 hover:underline">← Back to Students</Link></div>
      <PageHeader
        kicker={`${student.year_group} · ${student.house} House`}
        title={`${student.first_name} ${student.last_name}`}
        subtitle={`Admission ${student.admission_no} · ${student.nationality || ""}`}
      />

      <div className="grid md:grid-cols-3 gap-6">
        <div className="hl-card p-5 space-y-3">
          <div>
            <div className="overline">Profile</div>
          </div>
          <Row label="Date of birth" value={student.date_of_birth} />
          <Row label="Gender" value={student.gender} />
          <Row label="Boarding" value={student.boarding ? "Boarder" : "Day"} />
          <Row label="Scholarship" value={student.scholarship || "—"} />
          <Row label="Medical notes" value={student.medical_notes || "None recorded"} />
          <Row label="Status" value={<StatusPill status={student.status} />} />
        </div>

        <div className="hl-card p-5 md:col-span-2">
          <div className="overline">Academic Overview · {report?.term || "Current Term"}</div>
          <h3 className="font-display font-bold text-xl tracking-tight mt-1">
            {report ? `Term average: ${report.average}%` : "Report card"}
          </h3>
          <div className="text-xs text-slate-500">
            Attendance: {report?.attendance_pct ?? "—"}%
          </div>
          <div className="mt-4">
            <DataTable
              testId="report-card-subjects"
              columns={[
                { key: "subject_name", label: "Subject" },
                { key: "average", label: "Average", render: (r) => <span className="mono">{r.average}%</span> },
                { key: "grade_letter", label: "Grade" },
              ]}
              rows={report?.subjects || []}
              empty="No grades yet"
            />
          </div>
        </div>
      </div>

      <Section title="Recent Attendance" testId="student-attendance">
        <DataTable
          columns={[
            { key: "date", label: "Date" },
            { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
            { key: "note", label: "Note", render: (r) => r.note || <span className="text-slate-400">—</span> },
          ]}
          rows={attendance}
          empty="No attendance records"
        />
      </Section>

      <Section title="Fees & Invoices" testId="student-invoices">
        <DataTable
          columns={[
            { key: "term", label: "Term" },
            { key: "total", label: "Total", render: (r) => <span className="mono">${r.total.toLocaleString()}</span> },
            { key: "amount_paid", label: "Paid", render: (r) => <span className="mono">${r.amount_paid.toLocaleString()}</span> },
            { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
            { key: "due_date", label: "Due" },
          ]}
          rows={invoices}
        />
      </Section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm border-b border-slate-100 last:border-0 py-1.5">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium text-right">{value}</div>
    </div>
  );
}
